/**
 * Tradução EN → PT-BR para PDF Report
 * Garante conteúdo completo em português quando _pt está ausente no JSON.
 *
 * Limites APIs gratuitas: MyMemory ~5k chars/dia (anônimo), LibreTranslate rate-limited.
 * Estratégia: dicionário primeiro, pular células numéricas, chunks menores, delay entre chamadas.
 */

const DICT_EN_PT = {
  'Executive Summary': 'Sumário Executivo',
  'Key Findings': 'Principais Conclusões',
  'Country': 'País',
  'Debt/GDP 2025': 'Dívida/PIB 2025',
  'Interest/GDP': 'Juros/PIB',
  'Primary Balance': 'Resultado Primário',
  'Item': 'Item',
  'Official 2025': 'Oficial 2025',
  'Off-Book': 'Off-Book',
  'Adjusted': 'Ajustado',
  'Year': 'Ano',
  'Base': 'Base',
  'Bull': 'Bull',
  'Bear': 'Bear',
  'Triggers': 'Gatilhos',
  'Episode': 'Episódio',
  'USDBRL Low': 'USDBRL Mín',
  'Peak': 'Pico',
  'Change': 'Variação',
  'Sources': 'Fontes',
  'N/A': 'N/D',
  'Primary Deficit': 'Déficit Primário',
  'Electoral cycle': 'Ciclo eleitoral',
  'Reform outlook': 'Perspectiva de reformas',
  'Stress test': 'Teste de estresse',
  'Market': 'Mercado',
  'Dynamics': 'Dinâmicas',
  // WLCC / Financial
  'Business Model': 'Modelo de Negócio',
  'Program Structure': 'Estrutura do Programa',
  'Revenue Streams': 'Fontes de Receita',
  'Source': 'Fonte',
  'Fee/Share': 'Taxa/Participação',
  'Description': 'Descrição',
  '12-Month Financial Projections': 'Projeções Financeiras 12 Meses',
  'Annual Summary': 'Resumo Anual',
  'Fiat to Crypto Flow': 'Fluxo Fiat para Crypto',
  'Step': 'Passo',
  'Action': 'Ação',
  'FIDDUX Responsibility': 'Responsabilidade FIDDUX',
  'Cost': 'Custo',
  'Risk Mitigation': 'Mitigação de Riscos',
  'Risk': 'Risco',
  'Likelihood': 'Probabilidade',
  'Mitigation': 'Mitigação',
  'Recommendations': 'Recomendações',
  'Appendix': 'Apêndice',
  'Month': 'Mês',
  'Clients': 'Clientes',
  'Volume (EUR)': 'Volume (EUR)',
  'Costs': 'Custos',
  'Revenues': 'Receitas',
  'Profit': 'Lucro',
  'Net Position': 'Posição Líquida',
};

const DELAY_MS = 350;
// MyMemory rejeita queries acima de ~500 caracteres (erro "QUERY LENGTH LIMIT
// EXCEEDED. MAX ALLOWED QUERY : 500 CHARS"). Mantemos folga de segurança.
const MAX_QUERY_CHARS = 480;

function delay(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

/**
 * Quebra um texto em pedaços de no máximo MAX_QUERY_CHARS, respeitando limites
 * de frase quando possível e fazendo hard-split de frases muito longas.
 */
function chunkText(text, maxLen = MAX_QUERY_CHARS) {
  const pieces = text.split(/(?<=[.!?\n])\s+/);
  const chunks = [];
  for (const piece of pieces) {
    let p = piece;
    // Frase única maior que o limite: corta em pedaços de maxLen.
    while (p.length > maxLen) {
      chunks.push(p.slice(0, maxLen));
      p = p.slice(maxLen);
    }
    if (!p) continue;
    const last = chunks[chunks.length - 1];
    if (last && (last + ' ' + p).length <= maxLen) {
      chunks[chunks.length - 1] = last + ' ' + p;
    } else {
      chunks.push(p);
    }
  }
  return chunks;
}

function isQuotaOrLengthError(data) {
  const status = data?.responseStatus;
  const details = String(data?.responseDetails || '').toUpperCase();
  if (status === 429) return true;
  if (details.includes('QUERY LENGTH LIMIT EXCEEDED')) return true;
  if (typeof status === 'number' && status >= 400) return true;
  return false;
}

async function translateChunkWithAPI(trimmed) {
  await delay(DELAY_MS);
  try {
    const res = await fetch(
      `https://api.mymemory.translated.net/get?q=${encodeURIComponent(trimmed)}&langpair=en|pt-br`
    );
    const data = await res.json();
    if (isQuotaOrLengthError(data)) {
      throw new Error('QUOTA_EXCEEDED');
    }
    const translated = data?.responseData?.translatedText;
    return translated || trimmed;
  } catch (e) {
    if (e?.message === 'QUOTA_EXCEEDED') throw e;
    try {
      await delay(DELAY_MS);
      const res = await fetch('https://libretranslate.com/translate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ q: trimmed, source: 'en', target: 'pt' }),
      });
      if (!res.ok) throw new Error('API error');
      const data = await res.json();
      return data.translatedText || trimmed;
    } catch {
      return translateWithDict(trimmed) || trimmed;
    }
  }
}

async function translateWithAPI(text) {
  if (!text || typeof text !== 'string') return text;
  const trimmed = text.trim();
  if (!trimmed) return text;
  if (trimmed.length > MAX_QUERY_CHARS) {
    return translateLongText(trimmed);
  }
  return translateChunkWithAPI(trimmed);
}

async function translateLongText(text) {
  const chunks = chunkText(text, MAX_QUERY_CHARS);
  const results = [];
  for (const c of chunks) {
    results.push(await translateChunkWithAPI(c));
  }
  return results.join(' ');
}

function translateWithDict(text) {
  const t = text.trim();
  if (DICT_EN_PT[t]) return DICT_EN_PT[t];
  return null;
}

export async function translateToPt(text, onQuotaExceeded) {
  if (!text || typeof text !== 'string') return text;
  const dict = translateWithDict(text);
  if (dict) return dict;
  try {
    return await translateWithAPI(text);
  } catch (e) {
    if (e?.message === 'QUOTA_EXCEEDED') {
      if (onQuotaExceeded) onQuotaExceeded();
      return text;
    }
    throw e;
  }
}

export async function ensureSchemaHasPT(schema) {
  const meta = schema.report_meta || {};
  const tasks = [];
  let quotaExceeded = false;

  if (!meta.title_pt && meta.title) tasks.push(() => translateToPt(meta.title).then((t) => { meta.title_pt = t; }));
  if (!meta.cover_subtitle_pt && meta.cover_subtitle) tasks.push(() => translateToPt(meta.cover_subtitle).then((t) => { meta.cover_subtitle_pt = t; }));
  if (!meta.prepared_by_pt && meta.prepared_by) tasks.push(() => translateToPt(meta.prepared_by).then((t) => { meta.prepared_by_pt = t; }));
  if (!meta.exclusive_partners_pt && meta.exclusive_partners) tasks.push(() => translateToPt(meta.exclusive_partners).then((t) => { meta.exclusive_partners_pt = t; }));
  if (!meta.footer_branding_pt && meta.footer_branding) tasks.push(() => translateToPt(meta.footer_branding).then((t) => { meta.footer_branding_pt = t; }));
  const appendixSources = meta.appendix_sources || schema.appendix_sources || [];
  if (appendixSources.length && !meta.appendix_sources_pt) {
    tasks.push(async () => {
      meta.appendix_sources_pt = [];
      for (const s of appendixSources) {
        meta.appendix_sources_pt.push(await translateToPt(s));
      }
    });
  }

  for (const task of tasks) await task();

  async function ensureSection(s) {
    if (!s.title_pt && s.title) s.title_pt = await translateToPt(s.title);
    if (!s.content_pt && s.content) s.content_pt = await translateToPt(s.content);
    for (const tbl of s.tables || []) {
      if (!tbl.headers_pt && tbl.headers) {
        tbl.headers_pt = await Promise.all(tbl.headers.map((h) => translateToPt(h)));
      }
      if (!tbl.rows_pt && tbl.rows) {
        tbl.rows_pt = tbl.rows;
      }
    }
    for (const b of s.boxes || s.highlight_boxes || []) {
      if (!b.title_pt && b.title) b.title_pt = await translateToPt(b.title);
      if (!b.content_pt && b.content) b.content_pt = await translateToPt(b.content);
    }
    if (s.chart && !s.chart.title_pt && s.chart.title) s.chart.title_pt = await translateToPt(s.chart.title);
    for (const ss of s.subsections || []) await ensureSubsection(ss);
  }

  async function ensureSubsection(ss) {
    if (!ss.title_pt && ss.title) ss.title_pt = await translateToPt(ss.title);
    if (!ss.content_pt && ss.content) ss.content_pt = await translateToPt(ss.content);
    for (const tbl of ss.tables || []) {
      if (!tbl.headers_pt && tbl.headers) tbl.headers_pt = await Promise.all(tbl.headers.map((h) => translateToPt(h)));
      if (!tbl.rows_pt && tbl.rows) tbl.rows_pt = tbl.rows;
    }
    for (const b of ss.boxes || []) {
      if (!b.title_pt && b.title) b.title_pt = await translateToPt(b.title);
      if (!b.content_pt && b.content) b.content_pt = await translateToPt(b.content);
    }
    if (ss.chart && !ss.chart.title_pt && ss.chart.title) ss.chart.title_pt = await translateToPt(ss.chart.title);
  }

  for (const s of schema.sections || []) await ensureSection(s);
  return schema;
}
